import { createSlice } from "@reduxjs/toolkit";
import { Client } from "@dto/Client";
import { RootState } from "../store";
import { getDefaultMapZoom } from "@lib/getDefaultMapZoom";
import { getIsQueryStringTruthy } from "@lib/url";

/**
 * There is no client reducer, but mimic App repo until we can get off React on Rails.
 */
const initialState = (): Client => {
  const searchParameters = new URLSearchParams(location.search);
  const isKiosk = searchParameters.get("kiosk") === "true";

  return {
    id: VisitWidget.settings.currentClientId,
    name: VisitWidget.settings.currentClientName,
    widgetLogoUrl: VisitWidget.settings.logoUrl,
    userAuthenticationFeature: {
      numberOfPlanAdditionsToPromptSignup:
        VisitWidget.settings.numberOfPlanAdditionsToPromptSignup,
      newsletterDefaultValue: VisitWidget.settings.isNewsletterCheckedByDefault,
      requirePostalCode: VisitWidget.settings.isRequirePostalCode,
      allowGoogleAuth: VisitWidget.settings.allowGoogleAuth,
      allowPhoneAuth: VisitWidget.settings.allowPhoneAuth,
    },
    privacyPolicyUrl: VisitWidget.settings.privacyPolicyUrl,
    mapZoomLevel: getDefaultMapZoom(),
    locales: VisitWidget.settings.currentClientLocales,
    categoriesVisibleByMenuItem: VisitWidget.store.categoriesVisibleByMenuItem,
    newsletters: VisitWidget.settings.newsletters,
    kioskFeature: {
      enabled: VisitWidget.settings.isKioskEnabled && isKiosk,
      homeMenuEnabled: VisitWidget.settings.isKioskHomeMenuEnabled && isKiosk,
      menuImageUrlLandscape:
        VisitWidget.settings.kioskFeature.menuImageUrlLandscape,
      menuImageUrlPortrait:
        VisitWidget.settings.kioskFeature.menuImageUrlPortrait,
      menuBackgroundColor:
        VisitWidget.settings.kioskFeature.menuBackgroundColor,
      shadow: VisitWidget.settings.kioskFeature.shadow,
      labelPosition: VisitWidget.settings.kioskFeature.labelPosition,
      secondsBeforeSessionReset:
        VisitWidget.settings.kioskFeature.secondsBeforeSessionReset,
      textColor: VisitWidget.settings.kioskFeature.textColor,
      isPhoneEnabled: VisitWidget.settings.isKioskPhoneEnabled,
      isWebsitesEnabled: VisitWidget.settings.isKioskWebsitesEnabled,
    },
    appSmartBanner: {
      appIcon: VisitWidget.smartAppBanner?.appIcon,
      appTitle: VisitWidget.smartAppBanner?.appTitle,
      appleAppId: VisitWidget.smartAppBanner?.appleAppId,
      googleAppId: VisitWidget.smartAppBanner?.googleAppId,
      isShowOnAndroid: VisitWidget.smartAppBanner?.isShowOnAndroid ?? false,
      isShowOnDesktop: VisitWidget.smartAppBanner?.isShowOnDesktop ?? false,
      isShowOnIOS: VisitWidget.smartAppBanner?.isShowOnIOS ?? false,
      minutesToKeepClosed: VisitWidget.smartAppBanner?.minutesToKeepClosed,
    },
    embeddedViewOptions: {
      /**
       * Hide the header (menu items, search, plan, login/profile). Also, only allow user to
       * navigate the entity/menu item selected in url path and its children.
       */
      isHideHeader: getIsQueryStringTruthy("hide_header"),
      /**
       * Disable entity lists, can be disabled on mobile with `ignore_disable_lists_on_mobile`.
       */
      isDisableLists: getIsQueryStringTruthy("disable_lists"),
      /**
       * Ignore disable lists on mobile.
       */
      isIgnoreDisableListsOnMobile: getIsQueryStringTruthy(
        "ignore_disable_lists_on_mobile",
      ),
      /**
       * Hide App Smart Banner.
       */
      isDisableAppBanner: getIsQueryStringTruthy("disable_app_banner"),
      /**
       * Set map as default view when on mobile.
       */
      isMapAsDefaultView: getIsQueryStringTruthy("map_as_default_view"),
    },
    adsOn: VisitWidget.settings.adsOn,
    adNthPosition: VisitWidget.settings.adNthPosition,
    adsCacheVersion: VisitWidget.settings.adsCacheVersion,
    geographicPaginationEnabled:
      VisitWidget.settings.geographicPaginationEnabled,
    tripBuilderEnabled: VisitWidget.settings.tripBuilderEnabled,
    userPreferences: VisitWidget.settings.userPreferences,
  };
};

export const clientSlice = createSlice({
  name: "client",
  initialState: initialState(),
  reducers: {},
});

export const selectClient = (state: RootState) => {
  return state.client;
};

export const selectIsKioskEnabled = (state: RootState) => {
  return state.client.kioskFeature.enabled;
};

export const selectAppSmartBanner = (state: RootState) => {
  return state.client.appSmartBanner;
};

export const selectIsDisableAppBanner = (state: RootState) => {
  return state.client.embeddedViewOptions.isDisableAppBanner;
};

export const selectIsDisableLists = (state: RootState) => {
  return state.client.embeddedViewOptions.isDisableLists;
};

export const selectAdsOn = (state: RootState) => {
  return state.client.adsOn;
};

export const selectAdNthPosition = (state: RootState) => {
  return state.client.adNthPosition;
};

export const selectAdsCacheVersion = (state: RootState) => {
  return state.client.adsCacheVersion;
};

export const selectIsPhoneEnabled = (state: RootState) => {
  if (selectIsKioskEnabled(state)) {
    return state.client.kioskFeature.isPhoneEnabled;
  } else {
    return true;
  }
};

export const selectIsWebsitesEnabled = (state: RootState) => {
  if (selectIsKioskEnabled(state)) {
    return state.client.kioskFeature.isWebsitesEnabled;
  } else {
    return true;
  }
};

export const selectIsTripBuilderEnabled = (state: RootState) => {
  return state.client.tripBuilderEnabled;
};

export const selectClientUserPreferences = (state: RootState) => {
  return state.client.userPreferences;
};

// eslint-disable-next-line import/no-default-export
export default clientSlice.reducer;
